'use client'

import { useEffect, useRef } from 'react'

/**
 * Background hero video that plays at a slowed-down rate for a more
 * cinematic feel. The `playbackRate` HTML attribute doesn't exist, so it
 * has to be set imperatively via a ref after the element mounts (and re-set
 * on `loadedmetadata`, since some browsers reset the rate when metadata
 * arrives).
 */
type Props = {
  src: string
  poster?: string
  /** Multiplier for the playback rate. Default 0.5 = half speed. */
  rate?: number
  className?: string
}

export default function HeroVideo({
  src,
  poster,
  rate = 0.5,
  className = '',
}: Props) {
  const ref = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const v = ref.current
    if (!v) return
    const apply = () => {
      v.playbackRate = rate
    }
    apply()
    v.addEventListener('loadedmetadata', apply)
    v.addEventListener('play', apply)
    return () => {
      v.removeEventListener('loadedmetadata', apply)
      v.removeEventListener('play', apply)
    }
  }, [rate])

  return (
    <video
      ref={ref}
      className={className}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      poster={poster}
      // Hint browsers to render the video on its own layer so the slowed
      // playback stays smooth (avoids the main thread hitching on each
      // frame swap).
      style={{ willChange: 'transform', transform: 'translateZ(0)' }}
    >
      <source src={src} type="video/mp4" />
    </video>
  )
}
